import CallIcon from '@mui/icons-material/Call';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type FC, useEffect, useRef, useState } from 'react';
import { useGetClientCallsQuery, useUpdateEditableAttributesMutation } from 'services/api/clients.api';
import {
  useGetCallAudioUploadUrlMutation,
  useInitiateCallMutation,
  useSendCallPermissionMutation,
  useTerminateCallMutation,
} from 'services/api/whatsAppCalling.api';
import type { Client } from 'services/types/client';
import { getPusherClient, subscribeChannel } from 'utils/pusherClient';

import MicrophoneSelector from '../../components/MicrophoneSelector';
import useMicrophone from '../../hooks/useMicrophone';
import CallsHistorySection from './components/CallsHistorySection';
import InfoItem from './components/InfoItem';

const MIN_CHUNK_SIZE = 262144; // Minimum chunk size required by Google Cloud Storage for resumable uploads (256 KB)

interface Props {
  client: Client;
  isConversationActive: boolean;
}

const CallManager: FC<Props> = ({ client, isConversationActive }) => {
  const [sendCallPermission] = useSendCallPermissionMutation();
  const [terminateCall] = useTerminateCallMutation();
  const [initiateCall] = useInitiateCallMutation();
  const [getCallAudioUploadUrl] = useGetCallAudioUploadUrlMutation();
  const [updateEditableAttributes, { isLoading: isSavingNotes }] = useUpdateEditableAttributesMutation();
  const { data: clientCallsData, refetch: refetchClientCalls } = useGetClientCallsQuery(String(client.id), {
    skip: !client.id,
  });
  const clientCalls = clientCallsData?.calls;

  const [notes, setNotes] = useState(client.notes || '');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [hasCallPermission, setHasCallPermission] = useState<boolean>(client.hasCallPermissionNow || false);

  const {
    stream: localStream,
    isActive: isMicrophoneActive,
    error: microphoneError,
    devices,
    selectedDeviceId,
    accessMicrophone,
    freeMicrophone,
    setSelectedDeviceId,
    refreshDevices,
  } = useMicrophone({
    audioConstraints: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 48000, // WhatsApp prefers 48kHz
      channelCount: 1, // Mono audio
    },
    autoAccess: false,
  });

  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const uploadInfoRef = useRef<{ uploadUrl: string; fileName: string; callId: number } | null>(null);
  const sessionUriRef = useRef<string | null>(null);
  const uploadedBytesRef = useRef<number>(0);
  const chunkBufferRef = useRef<Blob[]>([]);
  const chunkBufferSizeRef = useRef<number>(0);
  // I dont think this queue is needed
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve());

  const [callStatus, setCallStatus] = useState<
    'idle' | 'connecting' | 'connected' | 'terminated' | 'rejected' | 'failed'
  >('idle');
  const [callError, setCallError] = useState<string | null>(null);
  const [pendingSdpAnswer, setPendingSdpAnswer] = useState<{
    sdpAnswer: string;
    callId: string;
    timestamp: string;
  } | null>(null);
  const [callDuration, setCallDuration] = useState<number | null>(null);

  const resetUploadState = () => {
    remoteStreamRef.current = null;
    sessionUriRef.current = null;
    uploadInfoRef.current = null;
    uploadedBytesRef.current = 0;
    chunkBufferRef.current = [];
    chunkBufferSizeRef.current = 0;
    uploadQueueRef.current = Promise.resolve();
  };

  const initiateUploadSession = async (uploadInfo: {
    uploadUrl: string;
    fileName: string;
    callId: number;
  }): Promise<string> => {
    try {
      const initResponse = await fetch(uploadInfo.uploadUrl, {
        method: 'POST',
        headers: {
          'X-Goog-Resumable': 'start',
          'Content-Type': 'audio/webm',
        },
      });
      if (!initResponse.ok) {
        throw new Error(`Failed to initiate upload: ${initResponse.statusText}`);
      }
      const sessionUri = initResponse.headers.get('Location');
      if (!sessionUri) {
        throw new Error('No session URI received from upload initiation');
      }
      return sessionUri;
    } catch (error) {
      console.error('[CallManager] Error initiating upload session:', error);
      throw error;
    }
  };

  const uploadChunk = async (isFinal: boolean): Promise<void> => {
    // Wait for any previous upload to complete before starting a new one
    // This ensures uploads are sequential and uploadedBytesRef is always accurate
    await uploadQueueRef.current;

    // Create a new promise for this upload and add it to the queue
    const uploadPromise = (async (): Promise<void> => {
      // For debugging, if it works ok, delete this
      console.log('[CallManager] Uploading chunk', {
        isFinal,
        chunkBufferSize: chunkBufferSizeRef.current,
        uploadedBytes: uploadedBytesRef.current,
        isValidSize: chunkBufferSizeRef.current >= MIN_CHUNK_SIZE,
      });
      if (chunkBufferSizeRef.current >= MIN_CHUNK_SIZE || (isFinal && chunkBufferSizeRef.current > 0)) {
        if (!sessionUriRef.current || !uploadInfoRef.current) {
          throw new Error('Upload session not initialized');
        }
        const chunksToUpload = [...chunkBufferRef.current];
        // Clear buffer
        chunkBufferRef.current = [];
        chunkBufferSizeRef.current = 0;

        const chunk = new Blob(chunksToUpload, { type: 'audio/webm' });
        if (!isFinal && chunk.size < MIN_CHUNK_SIZE) {
          throw new Error(
            `Chunk size ${chunk.size} bytes is less than minimum required ${MIN_CHUNK_SIZE} bytes for resumable uploads`
          );
        }

        // Read uploadedBytesRef AFTER waiting for previous uploads to ensure it's up to date
        const startByte = uploadedBytesRef.current;
        let endByte = startByte + chunk.size - 1;

        const contentRange = isFinal
          ? `bytes ${startByte}-${endByte}/${endByte + 1}`
          : `bytes ${startByte}-${endByte}/*`;

        // TODO: Remove this after tests
        console.log('[CallManager] Uploading chunk with Content-Range:', contentRange, {
          startByte,
          endByte,
          chunkSize: chunk.size,
          isFinal,
        });

        try {
          const uploadResponse = await fetch(sessionUriRef.current, {
            method: 'PUT',
            headers: {
              'Content-Type': 'audio/webm',
              'Content-Length': chunk.size.toString(),
              'Content-Range': contentRange,
            },
            body: chunk,
          });
          const range = uploadResponse.headers.get('range');
          if (range) {
            const [, end] = range.split('=').pop()?.split('-') || [];
            endByte = parseInt(end, 10);
          }

          // 308 Resume Incomplete is expected for intermediate chunks
          // 200 OK means upload is complete
          if (!uploadResponse.ok && uploadResponse.status !== 308) {
            throw new Error(`Failed to upload chunk: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }
          // TODO: Remove this after tests
          console.log('[CallManager] Uploaded chunk successfully', {
            isFinal,
            status: uploadResponse.status,
            startByte,
            endByte,
            uploadedBytesRefBefore: uploadedBytesRef.current,
            uploadedBytesRefAfter: endByte + 1,
          });
          // Update uploaded bytes counter with the end byte of the response. This can mismatch with the sent size
          // We need to update this even for final chunks to ensure Content-Range is correct
          uploadedBytesRef.current = endByte + 1;

          // Don't reset upload state here when isFinal is true - we need to keep the session
          // for potential additional final chunks. Reset will happen in cleanupWebRTCResources.
        } catch (error) {
          console.error('[CallManager] Error uploading chunk:', error);
          throw error;
        }
      }
    })();

    // Even if upload fails, we want subsequent uploads to proceed
    uploadQueueRef.current = uploadPromise.catch((e) => {
      console.error('[CallManager] Error uploading chunk:', e);
    });

    return uploadPromise;
  };

  const startAudioRecording = async (localAudioStream: MediaStream, remoteAudioStream: MediaStream) => {
    const currentRecorder = mediaRecorderRef.current;
    if (currentRecorder && currentRecorder.state !== 'inactive') {
      console.log('[CallManager] Recording already in progress, skipping to prevent multiple recordings');
      return;
    }
    if (!MediaRecorder.isTypeSupported('audio/webm')) {
      throw new Error('audio/webm is not supported');
    }

    try {
      const { data: uploadInfo } = await getCallAudioUploadUrl({ clientId: client.id.toString() });
      if (!uploadInfo) {
        throw new Error('Failed to get upload URL');
      }

      uploadInfoRef.current = uploadInfo;

      // Combine both audio streams into one for recording using AudioContext
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      // Create source nodes for both streams
      const localSource = audioContext.createMediaStreamSource(localAudioStream);
      const remoteSource = audioContext.createMediaStreamSource(remoteAudioStream);
      // Connect both sources to the destination to mix them
      localSource.connect(destination);
      remoteSource.connect(destination);
      const recorder = new MediaRecorder(destination.stream, {
        mimeType: 'audio/webm',
        audioBitsPerSecond: 128000, // 128kbps for good quality
      });

      const sessionUri = await initiateUploadSession(uploadInfo);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = async (event) => {
        // Process all chunks, including the final one when recorder is stopping
        if (event.data && event.data.size > 0) {
          const chunk = event.data;
          chunkBufferRef.current.push(chunk);
          chunkBufferSizeRef.current += chunk.size;

          // Only mark as final if recorder is inactive (from stop() call)
          // requestData() chunks should not be marked as final yet - stop() will provide the true final chunk
          const isFinal = recorder.state === 'inactive';
          // Track the upload promise so we can wait for it to complete
          await uploadChunk(isFinal);
        }
      };

      recorder.onstop = async () => {
        // Clean up audio context
        localSource.disconnect();
        remoteSource.disconnect();
        await audioContext.close();
      };

      recorder.onerror = (event) => {
        console.error('[CallManager] MediaRecorder error:', event);
        // Clean up audio context
        localSource.disconnect();
        remoteSource.disconnect();
        audioContext.close().catch(() => {});
      };

      sessionUriRef.current = sessionUri;
      uploadedBytesRef.current = 0;
      chunkBufferRef.current = [];
      chunkBufferSizeRef.current = 0;
      recorder.start(5000); // Start recording with timeslice to get chunks periodically every 5 seconds
    } catch (error) {
      console.error('[CallManager] Error starting audio recording:', error);
    }
  };

  const cleanupWebRTCResources = async () => {
    const currentRecorder = mediaRecorderRef.current;
    // Stop media recorder if active - this will trigger ondataavailable with final chunk
    if (currentRecorder && currentRecorder.state !== 'inactive') {
      // Upload any buffered chunks before requesting final data
      if (chunkBufferSizeRef.current > 0) {
        await uploadChunk(false).catch((uploadError) => {
          console.error('[CallManager] Error uploading buffered chunks before stop:', uploadError);
        });
      }

      // Request any remaining data immediately (this triggers ondataavailable with current buffer)
      // This gets data that hasn't reached the 5-second timeslice threshold
      // Note: This chunk will NOT be marked as final - stop() will provide the true final chunk
      try {
        // Requesting remaining data from recorder
        currentRecorder.requestData();
        // Wait a bit for the requestData to trigger ondataavailable
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 200);
        });
      } catch (requestError) {
        console.warn('[CallManager] Error requesting data (may not be supported):', requestError);
      }
      // Now stop the recorder - this will also trigger a final ondataavailable event
      currentRecorder.stop();
    }

    // Clear ref after stopping
    mediaRecorderRef.current = null;

    // Final check to upload any remaining data as a safety net
    if (chunkBufferSizeRef.current > 0 && sessionUriRef.current && uploadInfoRef.current) {
      await uploadChunk(true).catch((uploadError) => {
        console.error('[CallManager] Error during final safety net upload:', uploadError);
      });
    }

    // Now reset upload state after all chunks are uploaded
    resetUploadState();

    // Clean up remote audio element
    if (remoteAudioElementRef.current) {
      try {
        remoteAudioElementRef.current.pause();
        remoteAudioElementRef.current.srcObject = null;
        if (remoteAudioElementRef.current.parentNode) {
          remoteAudioElementRef.current.parentNode.removeChild(remoteAudioElementRef.current);
        }
      } catch (error) {
        console.error('[CallManager] Error removing remote audio element:', error);
      }
      remoteAudioElementRef.current = null;
    }

    // Stop remote stream tracks
    if (remoteStreamRef.current) {
      try {
        remoteStreamRef.current.getTracks().forEach((track) => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
      } catch (error) {
        console.error('[CallManager] Error stopping remote stream tracks:', error);
      }
    }

    if (peerConnection) {
      try {
        // Get all tracks from senders before removing them
        const tracksToStop: MediaStreamTrack[] = [];
        peerConnection.getSenders().forEach((sender) => {
          if (sender.track) {
            tracksToStop.push(sender.track);
            peerConnection.removeTrack(sender);
          }
        });

        // Stop all tracks that were removed from senders
        tracksToStop.forEach((track) => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });

        // Remove all transceivers to stop media flow
        peerConnection.getTransceivers().forEach((transceiver) => {
          if (transceiver.stop) {
            transceiver.stop();
          }
        });
        peerConnection.close();
      } catch (error) {
        console.error('[CallManager] Error closing peer connection:', error);
      }

      setPeerConnection(null);
    }

    if (localStream) {
      try {
        localStream.getTracks().forEach((track) => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
      } catch (error) {
        console.error('[CallManager] Error stopping local stream tracks:', error);
      }
    }

    // Free microphone through the hook (this will also stop tracks and clear state)
    freeMicrophone();

    // Clear any pending SDP answer
    setPendingSdpAnswer(null);
    setCallError(null);
    resetUploadState();
  };

  useEffect(() => {
    const pusher = getPusherClient();
    const connectionErrorHandler = (error: unknown) => {
      console.error('[CallManager] Pusher connection error:', error);
    };
    pusher.connection.bind('error', connectionErrorHandler);

    const channelName = `call-status-${client.id}`;
    const { channel, unsubscribe } = subscribeChannel(channelName);

    const subscriptionErrorHandler = (error: unknown) => {
      console.error('[CallManager] Failed to subscribe to channel:', channelName, error);
    };
    channel.bind('pusher:subscription_error', subscriptionErrorHandler);

    const callStatusHandler = (data: {
      status: 'ringing' | 'accepted' | 'rejected' | 'terminated' | 'failed';
      timestamp: string;
      callId?: string;
      error?: string;
      duration?: number;
    }) => {
      console.log('[CallManager] Call status update received:', {
        status: data.status,
        timestamp: data.timestamp,
        error: data?.error,
      });
      switch (data.status) {
        case 'ringing':
          setCallStatus('connecting');
          break;
        case 'accepted':
          setCallStatus('connected');
          break;
        case 'rejected':
          setCallStatus('rejected');
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after rejection:', error);
          });
          refetchClientCalls().catch((error) => console.error('[CallManager] refetch calls failed:', error));
          break;
        case 'terminated':
          setCallStatus('terminated');
          setCallDuration(data.duration || null);
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after termination:', error);
          });
          refetchClientCalls().catch((error) => console.error('[CallManager] refetch calls failed:', error));
          break;
        case 'failed':
          setCallStatus('failed');
          setCallError(data.error || 'Call failed');
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after failure:', error);
          });
          refetchClientCalls().catch((error) => console.error('[CallManager] refetch calls failed:', error));
          break;
        default:
          console.warn('[CallManager] Unknown call status:', data.status);
          break;
      }
    };
    channel.bind('call-status-update', callStatusHandler);

    const sdpAnswerHandler = (data: { sdpAnswer: string; callId: string; timestamp: string }) => {
      if (peerConnection && localStream) {
        // Set the remote description (WhatsApp's SDP answer)
        peerConnection
          .setRemoteDescription({
            type: 'answer',
            sdp: data.sdpAnswer,
          })
          .catch((error) => {
            console.error('[CallManager] Error setting remote description:', error);
            setCallStatus('failed');
            setCallError('Failed to establish WebRTC connection');
          });
      } else {
        // Store the SDP answer to be processed when resources are ready
        setPendingSdpAnswer(data);
      }
    };
    channel.bind('sdp-answer-received', sdpAnswerHandler);

    const callPermissionHandler = (data: { callPermission: boolean; timestamp: string }) => {
      setHasCallPermission(data.callPermission);
    };
    channel.bind('call-permission-update', callPermissionHandler);

    return () => {
      channel.unbind('pusher:subscription_error', subscriptionErrorHandler);
      channel.unbind('call-status-update', callStatusHandler);
      channel.unbind('sdp-answer-received', sdpAnswerHandler);
      channel.unbind('call-permission-update', callPermissionHandler);
      pusher.connection.unbind('error', connectionErrorHandler);
      unsubscribe();
    };
  }, [client.id, refetchClientCalls]);

  // Clean up WebRTC resources when component unmounts or client changes
  useEffect(() => {
    return () => {
      cleanupWebRTCResources().catch((error) => {
        console.error('[CallManager] Error during cleanup on unmount:', error);
      });
    };
  }, [client.id]);

  // Process pending SDP answer when WebRTC resources become available
  useEffect(() => {
    if (pendingSdpAnswer && peerConnection && localStream) {
      peerConnection
        .setRemoteDescription({
          type: 'answer',
          sdp: pendingSdpAnswer.sdpAnswer,
        })
        .then(() => {
          setPendingSdpAnswer(null);
        })
        .catch((error) => {
          console.error('[CallManager] Error setting remote description from pending SDP answer:', error);
          setCallStatus('failed');
          setCallError('Failed to establish WebRTC connection');
          setPendingSdpAnswer(null);
        });
    }
  }, [pendingSdpAnswer, peerConnection, localStream]);

  const handleInitiateCall = async () => {
    try {
      setCallStatus('connecting');
      setCallError(null);

      // Access microphone and get the stream directly
      let microphoneStream: MediaStream;
      try {
        microphoneStream = await accessMicrophone();
      } catch (error) {
        console.error('[CallManager] Error setting up microphone:', error);
        setCallError(error instanceof Error ? error.message : 'Failed to set up microphone');
        throw error; // Re-throw to prevent continuing with call setup
      }

      // Validate that we have a valid stream
      if (!microphoneStream || microphoneStream.getTracks().length === 0) {
        throw new Error('Microphone stream not available. Please configure microphone first.');
      }

      const newPeerConnection = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }],
        iceCandidatePoolSize: 10,
      });

      // Handle incoming audio tracks from WhatsApp
      newPeerConnection.ontrack = (event) => {
        if (event.streams && event.streams.length > 0) {
          const remoteStream = event.streams[0];
          remoteStreamRef.current = remoteStream;

          // Create audio element to play incoming audio
          const audioElement = document.createElement('audio');
          audioElement.srcObject = remoteStream;
          audioElement.autoplay = true;
          audioElement.controls = false;
          audioElement.volume = 1.0;
          audioElement.muted = false;
          // Hide the audio element from the user
          audioElement.style.position = 'absolute';
          audioElement.style.left = '-9999px';
          audioElement.style.visibility = 'hidden';
          document.body.appendChild(audioElement);
          remoteAudioElementRef.current = audioElement;

          audioElement.play().catch((error) => {
            console.error('[CallManager] Error playing remote audio:', error);
          });

          // Start recording when we have both local and remote streams
          // Only start if not already recording (check both mediaRecorderRef state and uploadInfoRef)
          const currentRecorder = mediaRecorderRef.current;
          if (
            microphoneStream &&
            remoteStream &&
            (!currentRecorder || currentRecorder.state === 'inactive') &&
            !uploadInfoRef.current
          ) {
            startAudioRecording(microphoneStream, remoteStream).catch((error) => {
              console.error('[CallManager] Error starting audio recording:', error);
            });
          }
        } else {
          console.warn('[CallManager] No streams in remote track event');
        }
      };

      newPeerConnection.onconnectionstatechange = () => {
        if (newPeerConnection.connectionState === 'connected') {
          console.log('[CallManager] WebRTC connection established successfully');
        } else if (newPeerConnection.connectionState === 'failed') {
          console.error('[CallManager] WebRTC connection failed');
          setCallStatus('failed');
          setCallError('WebRTC connection failed');
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after WebRTC failure:', error);
          });
        } else if (newPeerConnection.connectionState === 'disconnected') {
          console.error('[CallManager] WebRTC connection disconnected');
          setCallStatus('terminated');
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after WebRTC disconnection:', error);
          });
        }
      };

      newPeerConnection.oniceconnectionstatechange = () => {
        if (newPeerConnection.iceConnectionState === 'failed') {
          console.error('[CallManager] ICE connection failed');
          setCallStatus('failed');
          setCallError('ICE connection failed');
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after ICE failure:', error);
          });
        } else if (newPeerConnection.iceConnectionState === 'disconnected') {
          console.error('[CallManager] ICE connection disconnected');
          setCallStatus('terminated');
          cleanupWebRTCResources().catch((error) => {
            console.error('[CallManager] Error during cleanup after ICE disconnection:', error);
          });
        }
      };

      const audioTracks = microphoneStream.getTracks();
      audioTracks.forEach((track) => {
        newPeerConnection.addTrack(track, microphoneStream);
      });

      // Configure transceivers to prioritize Opus codec if available
      const transceivers = newPeerConnection.getTransceivers();
      transceivers.forEach((transceiver) => {
        if (transceiver.receiver.track?.kind === 'audio') {
          try {
            const capabilities = RTCRtpReceiver.getCapabilities('audio');
            if (capabilities?.codecs) {
              const opusCodec = capabilities.codecs.find(
                (codec) => codec.mimeType === 'audio/opus' && codec.clockRate === 48000
              );
              if (opusCodec) {
                // Set codec preferences to prioritize Opus
                transceiver.setCodecPreferences([opusCodec]);
              } else {
                console.warn('[CallManager] Opus codec not available, using browser default');
              }
            }
          } catch (error) {
            console.warn('[CallManager] Could not set codec preferences:', error);
          }
        }
      });

      const offer = await newPeerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });

      // Modify SDP to enforce WhatsApp requirements
      if (offer.sdp) {
        let modifiedSdp = offer.sdp;
        try {
          // Add ptime=20 to the media line
          modifiedSdp = modifiedSdp.replace(/m=audio.*\r\n/g, (match) =>
            match.replace(/(m=audio.*)/, '$1\r\na=ptime:20')
          );
          // Add Opus codec constraints and DTMF configuration
          modifiedSdp = modifiedSdp.replace(
            /(a=rtpmap:\d+ opus\/48000\/1)/g,
            '$1\r\na=fmtp:$1 maxplaybackrate=48000; sprop-maxcapturerate=48000; maxaveragebitrate=510000; stereo=0; useinbandfec=1; usedtx=1; ptime=20'
          );
          // Add DTMF configuration for 8 kHz clock rate
          modifiedSdp = modifiedSdp.replace(/(a=rtpmap:\d+ telephone-event\/8000)/g, '$1\r\na=fmtp:$1 0-16');
          // Use the modified SDP in the offer
          offer.sdp = modifiedSdp;
        } catch (error) {
          console.warn('[CallManager] SDP modification failed, using original SDP:', error);
        }
      }

      await newPeerConnection.setLocalDescription(offer);

      await new Promise<void>((resolve) => {
        if (newPeerConnection.iceGatheringState === 'complete') {
          resolve();
          return;
        }

        // Safety net: ICE gathering can stall forever (blocked STUN, missed
        // 'complete' event). Proceed after 3s with whatever candidates we have.
        const timeoutId = setTimeout(() => {
          console.warn('[CallManager] ICE gathering timed out, proceeding with partial candidates');
          resolve();
        }, 3000);

        newPeerConnection.addEventListener('icegatheringstatechange', () => {
          if (newPeerConnection.iceGatheringState === 'complete') {
            clearTimeout(timeoutId);
            resolve();
          }
        });
      });

      // IMPORTANT: Set the state variables BEFORE initiating the call
      // This ensures they are available when the SDP answer arrives
      setPeerConnection(newPeerConnection);
      const finalSdp = newPeerConnection.localDescription?.sdp || '';
      try {
        const result = await initiateCall({
          clientId: client.id.toString(),
          sdp: finalSdp,
        });
        if ('error' in result) {
          throw new Error(`Backend error: ${JSON.stringify(result.error)}`);
        }
      } catch (apiError) {
        console.error('[CallManager] Error calling initiateCall API:', apiError);
        throw new Error(`Failed to initiate call: ${apiError instanceof Error ? apiError.message : 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error setting up WebRTC for call:', error);
      setCallStatus('failed');
      // Provide specific error messages for common issues
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          setCallError('Microphone access denied by user');
        } else if (error.name === 'NotFoundError') {
          setCallError('No microphone found on device');
        } else if (error.name === 'NotSupportedError') {
          setCallError('WebRTC not supported in this browser');
        } else {
          setCallError(error.message);
        }
      }
      cleanupWebRTCResources().catch((cleanupError) => {
        console.error('[CallManager] Error during cleanup in handleInitiateCall:', cleanupError);
      });
    }
  };

  const handleSendCallPermission = () => {
    sendCallPermission({ clientId: client.id.toString() }).catch((e) =>
      console.error('Error sending call permission', e)
    );
  };

  const handleTerminateCall = () => {
    setCallStatus('terminated');
    // Clean up resources immediately to release microphone
    cleanupWebRTCResources().catch((error) => {
      console.error('[CallManager] Error during cleanup in handleTerminateCall:', error);
    });
    terminateCall({ clientId: client.id.toString() }).catch((e) => console.error('Error terminating call', e));
  };

  const formatCallDuration = (seconds: number | null): string => {
    if (!seconds) return 'Duración no disponible';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const handleCancelNotesEdit = () => {
    setNotes(client.notes || '');
    setIsEditingNotes(false);
  };

  const getCallStatusText = () => {
    switch (callStatus) {
      case 'idle':
        return 'Esperando llamada';
      case 'connecting':
        return 'Conectando...';
      case 'connected':
        return 'Llamada activa';
      case 'terminated':
        return 'Llamada terminada';
      case 'rejected':
        return 'Llamada rechazada';
      case 'failed':
        return 'Llamada fallida';
      default:
        return 'Esperando llamada';
    }
  };

  const getStatusColor = () => {
    switch (callStatus) {
      case 'idle':
        return 'default';
      case 'connecting':
        return 'warning';
      case 'connected':
        return 'success';
      case 'terminated':
        return 'info';
      case 'failed':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const hasCallHistory = Boolean(clientCalls && clientCalls.length > 0);
  const isTestOrDemoClient = client.isTestClient || client.phone?.includes('Demo');
  if (isTestOrDemoClient) {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Las llamadas no están disponibles para clientes de prueba o de demo.
          </Typography>
        </Box>
        {hasCallHistory && clientCalls && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Historial de Llamadas
            </Typography>
            <CallsHistorySection clientCalls={clientCalls} />
          </Box>
        )}
      </Box>
    );
  }

  if (!hasCallPermission) {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', pb: 1 }}>
          {isConversationActive ? (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Para realizar llamadas de voz necesitamos que el cliente autorice las llamadas en WhatsApp.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CallIcon />}
                onClick={handleSendCallPermission}
                size="large"
              >
                Enviar Permiso de Llamada
              </Button>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Chat con el cliente no está activo, debes enviarle una plantilla para comenzar una conversación para poder
              enviar un permiso de llamada.
            </Typography>
          )}
        </Box>
        {clientCalls && clientCalls.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Historial de Llamadas
            </Typography>
            <CallsHistorySection clientCalls={clientCalls} />
          </Box>
        )}
      </Box>
    );
  }

  if (callStatus === 'idle') {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CallIcon />}
            onClick={() => {
              handleInitiateCall().catch((e) => console.error('Error initiating call', e));
            }}
            size="large"
            sx={{ minWidth: '200px' }}
          >
            Iniciar Llamada
          </Button>
        </Box>
        {clientCalls && clientCalls.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Historial de Llamadas
            </Typography>
            <CallsHistorySection clientCalls={clientCalls} />
          </Box>
        )}
      </Box>
    );
  }
  // When call is terminated, only show status and duration
  if (callStatus === 'terminated' || callStatus === 'rejected') {
    return (
      <>
        <Grid container spacing={0.5}>
          <InfoItem
            label="Estado de la Llamada"
            value={<Chip label={getCallStatusText()} color={getStatusColor()} variant="filled" size="small" />}
          />
          {callDuration && <InfoItem label="Duración" value={formatCallDuration(callDuration)} />}
        </Grid>

        <Box sx={{ my: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CallIcon />}
            onClick={() => {
              // Reset call status to idle to allow new call
              setCallStatus('idle');
              setCallDuration(null);
              setCallError(null);
              handleInitiateCall().catch((e) => console.error('Error initiating call', e));
            }}
            size="large"
            sx={{ minWidth: '200px' }}
          >
            Llamar Nuevamente
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Grid container spacing={0.5}>
        <InfoItem
          label="Estado de la Llamada"
          value={<Chip label={getCallStatusText()} color={getStatusColor()} variant="filled" size="small" />}
        />
        {callDuration && <InfoItem label="Duración" value={formatCallDuration(callDuration)} />}

        {callError && (
          <InfoItem
            label="Error"
            value={
              <Typography variant="body2" color="error">
                {callError}
              </Typography>
            }
            fullWidth
          />
        )}

        {microphoneError && (
          <InfoItem
            label="Error de Micrófono"
            value={
              <Box>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {microphoneError}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {(() => {
                    if (microphoneError.includes('denied')) {
                      return 'Haz clic en el ícono del micrófono en la barra de direcciones del navegador para permitir el acceso.';
                    }
                    if (microphoneError.includes('not found')) {
                      return 'Verifica que tu micrófono esté conectado y funcionando correctamente.';
                    }
                    return 'Intenta refrescar la página o reiniciar el navegador.';
                  })()}
                </Typography>
              </Box>
            }
            fullWidth
          />
        )}
      </Grid>
      <Box sx={{ my: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {callStatus === 'connected' && (
          <Button variant="contained" color="error" onClick={handleTerminateCall}>
            Terminar Llamada
          </Button>
        )}
      </Box>
      {isMicrophoneActive && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="Micrófono Activo" color="success" size="small" icon={<CallIcon />} />
          <Button variant="text" color="error" size="small" onClick={freeMicrophone}>
            Desactivar
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <MicrophoneSelector
            selectedDeviceId={selectedDeviceId}
            onDeviceChange={setSelectedDeviceId}
            devices={devices}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => {
            accessMicrophone().catch((e) => console.error('Error accessing microphone:', e));
            refreshDevices().catch((e) => console.error('Error refreshing devices:', e));
          }}
          title="Actualizar Dispositivos"
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notas del Cliente
          </Typography>
          {!isEditingNotes ? (
            <IconButton size="small" onClick={() => setIsEditingNotes(true)} title="Editar notas">
              <EditIcon fontSize="small" />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => {
                  setIsEditingNotes(false);
                  updateEditableAttributes({
                    id: client.id,
                    body: { notes },
                  }).catch((e) => console.error('Error saving notes:', e));
                }}
                title="Guardar notas"
                color="primary"
              >
                {isSavingNotes ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
              </IconButton>
              <Button size="small" onClick={handleCancelNotesEdit} disabled={isSavingNotes} variant="text">
                Cancelar
              </Button>
            </Box>
          )}
        </Box>
        {isEditingNotes ? (
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            placeholder="Agregar notas sobre el cliente..."
            disabled={isSavingNotes}
            size="small"
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              minHeight: '60px',
              p: 1,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: '#f9f9f9',
              whiteSpace: 'pre-wrap',
            }}
          >
            {notes || 'Sin notas'}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default CallManager;
